import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { DemoArcade } from "../components/DemoArcade";
import "../styles/landing.css";

interface Row {
  id: string;
  name: string;
  score: number;
}

const SEED: Row[] = [
  { id: "voidwalker", name: "voidwalker", score: 184_220 },
  { id: "0xSpeed", name: "0xSpeed", score: 179_010 },
  { id: "nightraven", name: "nightraven", score: 171_500 },
  { id: "turbofish", name: "turbofish", score: 164_980 },
  { id: "marigold", name: "marigold", score: 158_300 },
  { id: "hexcrawler", name: "hexcrawler", score: 151_770 },
];

/** A live-feeling top-N that shuffles ranks on a timer — sells the "climb" idea. */
function LiveBoard() {
  const [rows, setRows] = useState<Row[]>(SEED);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    const t = setInterval(() => {
      setRows((cur) => {
        const next = cur.map((r) => ({ ...r }));
        const i = Math.floor(Math.random() * next.length);
        next[i].score += Math.floor(Math.random() * 9000) + 800;
        next.sort((a, b) => b.score - a.score);
        setFlash(next[i].id);
        setTimeout(() => setFlash(null), 700);
        return next;
      });
    }, 1700);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="board-mock reveal" style={{ animationDelay: "0.25s" }}>
      <div className="board-mock-head">
        <span className="title">Weekly High Scores</span>
        <span className="row" style={{ gap: 8 }}>
          <span className="dot" />
          <span className="title" style={{ color: "var(--mint)" }}>live · top 6</span>
        </span>
      </div>
      {rows.map((r, i) => (
        <div
          key={r.id}
          className={`bm-row ${i === 0 ? "top" : ""} ${flash === r.id ? "flash" : ""}`}
        >
          <span className="rk">{i === 0 ? "🏆" : i + 1}</span>
          <span className="nm">{r.name}</span>
          <span className="sc">{r.score.toLocaleString()}</span>
        </div>
      ))}
      <div className="faint mono" style={{ fontSize: 11.5, padding: "12px 12px 4px" }}>
        fully managed · 0.4ms p99 rank · live push
      </div>
    </div>
  );
}

const HERO_CODE = (
  <code>
    <span className="tok-c"># one line of intent — everything else defaults</span>
    {"\n"}
    <span className="tok-k">curl</span> -X POST https://api.laddr.gg/<span className="tok-n">v1</span>/leaderboards \{"\n"}
    {"  "}-H <span className="tok-s">"X-API-Key: lad_live_…"</span> \{"\n"}
    {"  "}-d <span className="tok-s">{'\'{ "name": "Weekly High Scores" }\''}</span>
    {"\n\n"}
    <span className="tok-c"># submit a score → get the new rank back instantly</span>
    {"\n"}
    <span className="tok-k">POST</span> /v1/leaderboards/weekly-high-scores/<span className="tok-m">scores</span>
    {"\n"}
    {"  "}{"{"} <span className="tok-s">"player_id"</span>: <span className="tok-s">"p42"</span>, <span className="tok-s">"score"</span>: <span className="tok-n">184220</span> {"}"}
    {"\n"}
    <span className="tok-c">→ {"{"} rank: 1, previous_rank: 3, changed: true {"}"}</span>
  </code>
);

export function Landing() {
  const yearRef = useRef(new Date().getFullYear());

  return (
    <>
      <nav className="lp-nav">
        <div className="container">
          <Link to="/"><Logo /></Link>
          <div className="lp-nav-links">
            <a href="#features" className="hide-sm">Features</a>
            <a href="#how" className="hide-sm">How it works</a>
            <a href="#pricing" className="hide-sm">Pricing</a>
            <a href="#docs" className="hide-sm">Docs</a>
            <Link to="/login" className="hide-sm">Sign in</Link>
            <Link to="/register" className="btn btn-primary btn-sm">Start free</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero">
        <div className="container hero-grid">
          <div>
            <span className="eyebrow reveal">Real-time leaderboards · as a service</span>
            <h1>
              <span className="reveal" style={{ display: "block", animationDelay: "0.04s" }}>
                Every game has
              </span>
              <span className="reveal" style={{ display: "block", animationDelay: "0.1s" }}>
                a ladder. <span className="accent">Climb it live.</span>
              </span>
            </h1>
            <p className="hero-sub reveal" style={{ animationDelay: "0.16s" }}>
              Laddr is real-time leaderboards as a service — fast, dead-simple, and priced
              for indies. Fully managed: we handle the infrastructure so you can ship your
              game. No enterprise sales calls, no lock-in, just leaderboards that scale.
            </p>
            <div className="hero-cta reveal" style={{ animationDelay: "0.22s" }}>
              <Link to="/register" className="btn btn-primary">Create a board in 60s →</Link>
              <a href="#how" className="btn btn-ghost">See the API</a>
            </div>
            <div className="hero-meta reveal" style={{ animationDelay: "0.3s" }}>
              <div className="stat"><b>&lt;1ms</b><span>rank · top-N · around-me</span></div>
              <div className="stat"><b>zero</b><span>split-brain · fully managed</span></div>
              <div className="stat"><b>$0</b><span>to start · no card</span></div>
            </div>
          </div>
          <LiveBoard />
        </div>
      </header>

      {/* TRUST STRIP */}
      <div className="trust">
        <div className="container">
          <span>REAL-TIME UPDATES</span>
          <span>DURABLE &amp; RELIABLE</span>
          <span>SCALES WITH YOUR GAME</span>
          <span>UNITY · UNREAL · GODOT · JS</span>
          <span>FULLY MANAGED CLOUD</span>
        </div>
      </div>

      {/* PLAYABLE DEMO ARCADE */}
      <DemoArcade />

      {/* FEATURES */}
      <section className="section" id="features">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Why Laddr</span>
            <h2>Built for the indies the big platforms forgot.</h2>
            <p>
              PlayFab and AccelByte are built for enterprise — with the pricing and sales
              calls to match. Laddr is the deliberately simpler, cheaper choice: real-time
              ranks, a tiny API, and no lock-in. The hard part isn't the sort, it's staying
              correct and fast as you grow. That's the whole product.
            </p>
          </div>

          <div className="feat-grid">
            <div className="feat span2">
              <div className="ic">⚡</div>
              <h3>Real-time leaderboards</h3>
              <p>
                Every board lives in memory for instant ranked queries at any size — rank,
                top-N, paginated listings, and ±window "me &amp; my neighbours" all respond
                in under a millisecond. Live subscriptions push deltas the instant a rank
                changes, fanned out across stateless gateways. No polling, no Redis to babysit.
              </p>
            </div>
            <div className="feat">
              <div className="ic">🪜</div>
              <h3>Priced for indies</h3>
              <p>
                Transparent, low pricing that scales with usage — not a per-seat enterprise
                contract. Generous free tier, one cheap paid step up. No surprise bills, no
                "talk to sales" wall in front of shipping your game.
              </p>
            </div>
            <div className="feat">
              <div className="ic">⌘</div>
              <h3>Dead-simple API</h3>
              <p>
                <code className="mono" style={{ color: "var(--mint)" }}>{'{ "id": "..." }'}</code>{" "}
                creates a board. Everything else has a sane default. One REST call to submit a
                score returns the new rank — no second round-trip.
              </p>
            </div>
            <div className="feat">
              <div className="ic">🛡️</div>
              <h3>Resilient by design</h3>
              <p>
                Single-writer correctness with automatic failover — if a node goes down its
                boards seamlessly rebuild elsewhere from the durable store. No split-brain,
                ever. We manage the infra; you manage the game.
              </p>
            </div>
            <div className="feat">
              <div className="ic">🔓</div>
              <h3>No lock-in</h3>
              <p>
                Plain REST + WebSocket. Your data is just records you can export at any time.
                No vendor-specific SDK required. Leave whenever — we'd rather earn your
                business than trap you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / CODE */}
      <section className="section" id="how" style={{ paddingTop: 30 }}>
        <div className="container split">
          <div>
            <span className="eyebrow">Integrate in minutes</span>
            <h2 style={{ fontSize: "clamp(28px,3.6vw,42px)", marginTop: 14 }}>
              From <span style={{ fontStyle: "italic", color: "var(--mint)" }}>zero</span> to a
              live board in two calls.
            </h2>
            <p className="muted" style={{ marginTop: 16, fontSize: 16 }}>
              A project API key, one POST to create the board, one to submit a score. Ranks
              come back on the write. Drop in a thin SDK for Unity, Unreal, Godot, or JS when
              you want reconnecting live subscriptions handled for you.
            </p>
            <div className="row wrap" style={{ marginTop: 22, gap: 10 }}>
              <span className="tag tag-mint">best-score mode</span>
              <span className="tag tag-mint">force overwrite</span>
              <span className="tag tag-mint">score increment</span>
              <span className="tag tag-violet">aggregate</span>
              <span className="tag">subscore tiebreaks</span>
              <span className="tag">seasonal reset</span>
            </div>
          </div>
          <div className="code-panel reveal">
            <div className="bar">
              <i /><i /><i />
              <span className="fn">quickstart.sh</span>
            </div>
            <pre>{HERO_CODE}</pre>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="section" style={{ paddingTop: 30 }} id="roadmap">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Today &amp; next</span>
            <h2>Leaderboards now. The rest of your live game, soon.</h2>
          </div>
          <div className="roadmap">
            <div className="rm live">
              <h4><span className="dot" style={{ display: "inline-block", marginRight: 8 }} />Leaderboards — live</h4>
              <p>Real-time ranks, seasons, tiebreakers, the full config surface, and a management dashboard.</p>
            </div>
            <div className="rm">
              <h4>Player accounts</h4>
              <p>Hosted identity &amp; profiles so your boards know who's who. On the roadmap.</p>
            </div>
            <div className="rm">
              <h4>Inventory &amp; economy</h4>
              <p>Items, currencies, and grants — the same no-lock-in, Postgres-durable approach. On the roadmap.</p>
            </div>
            <div className="rm">
              <h4>Client SDKs</h4>
              <p>Unity, Unreal, Godot, JS/TS, Rust — REST + reconnecting WebSocket, batteries included.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="pricing" style={{ paddingTop: 30 }}>
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Pricing</span>
            <h2>Priced for indies. Built to scale.</h2>
            <p>
              Transparent tiers, generous free usage, and metered overage that stays fair as
              you grow. No per-seat fees, and no sales call just to see a number.
            </p>
          </div>
          <div className="price-grid">
            <div className="price">
              <span className="plan">Free</span>
              <div className="amt">$0<small> / mo</small></div>
              <p className="faint" style={{ fontSize: 13 }}>For prototypes, jams &amp; hobby games.</p>
              <ul>
                <li>1 project, up to 10 leaderboards (1,000 entries each)</li>
                <li>10k score writes / mo</li>
                <li>Scoped API keys (read / write / admin)</li>
                <li>Community support · free forever, no card</li>
              </ul>
              <Link to="/register" className="btn btn-ghost" style={{ marginTop: "auto", justifyContent: "center" }}>Start free</Link>
            </div>
            <div className="price featured">
              <span className="plan" style={{ color: "var(--mint)" }}>Indie</span>
              <div className="amt">$10<small> / mo</small></div>
              <p className="faint" style={{ fontSize: 13 }}>For shipping live games. Scales with usage.</p>
              <ul>
                <li>Everything in Free, plus:</li>
                <li>Unlimited boards &amp; seasons</li>
                <li>5M writes / mo, then metered cheaply</li>
                <li>Live WebSocket subscriptions &amp; team members</li>
                <li>Email support — no contract, cancel anytime</li>
              </ul>
              <Link to="/register" className="btn btn-primary" style={{ marginTop: "auto", justifyContent: "center" }}>Start 14-day trial</Link>
            </div>
            <div className="price">
              <span className="plan">Pro</span>
              <div className="amt">Let's talk</div>
              <p className="faint" style={{ fontSize: 13 }}>When a hit takes off.</p>
              <ul>
                <li>Dedicated capacity &amp; isolated tenancy</li>
                <li>Multi-region board locality</li>
                <li>Custom data retention &amp; export SLA</li>
                <li>Priority support &amp; onboarding</li>
              </ul>
              <a href="#docs" className="btn btn-ghost" style={{ marginTop: "auto", justifyContent: "center" }}>Contact us</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container" id="docs">
        <div className="cta-band">
          <span className="eyebrow">Ship today</span>
          <h2 style={{ marginTop: 12 }}>Your players are already competitive.</h2>
          <p>Spin up a board, point your game at it, and watch the ranks climb in real time. Free to start, no card, no sales call.</p>
          <div className="row" style={{ justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <Link to="/register" className="btn btn-primary">Create your first board →</Link>
            <a href="#how" className="btn btn-ghost">Read the API docs</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="container">
          <div className="stack" style={{ gap: 12, maxWidth: 280 }}>
            <Logo />
            <p className="faint" style={{ fontSize: 13.5 }}>
              Real-time leaderboards as a service. Fast, dead-simple, no lock-in — and priced
              for indies.
            </p>
          </div>
          <div className="cols">
            <div className="col">
              <h5>Product</h5>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#roadmap">Roadmap</a>
              <Link to="/register">Sign up</Link>
            </div>
            <div className="col">
              <h5>Developers</h5>
              <a href="#how">API reference</a>
              <a href="#how">Quickstart</a>
              <a href="#docs">SDKs</a>
              <Link to="/login">Dashboard</Link>
            </div>
            <div className="col">
              <h5>Company</h5>
              <a href="#docs">About</a>
              <a href="#docs">Contact</a>
              <a href="#docs">Status</a>
            </div>
          </div>
        </div>
        <div className="container" style={{ marginTop: 28 }}>
          <span className="faint mono" style={{ fontSize: 12 }}>
            © {yearRef.current} Laddr · built on boring, reliable tech.
          </span>
        </div>
      </footer>
    </>
  );
}
